import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { isAuthenticated, isSessionConfirmed, confirmSession } from "../utils/session";

// Where a session the server did not accept is sent. "verify-face" and
// "verify-email" land on the existing verification screens with the account's
// email, exactly as a refused login does — no new route, no new flow.
function redirectFor(result) {
  if (result.status === "verify-face") {
    try {
      // The face page's own refresh-safe memory (see FaceVerification.jsx).
      localStorage.setItem("pendingFaceEmail", result.email || "");
    } catch {
      /* private mode — router state below still carries the email */
    }
    return <Navigate to="/face-verification" replace state={{ email: result.email }} />;
  }
  if (result.status === "verify-email") {
    return <Navigate to="/verify-otp" replace state={{ email: result.email }} />;
  }
  return <Navigate to="/" replace />;
}

function ProtectedRoute({ children }) {
  // Rejects a missing, malformed or expired (>7 days) token and clears it.
  const authed = isAuthenticated();

  // Confirmed once per token per page load; later navigations render at once.
  const [result, setResult] = useState(() =>
    authed && isSessionConfirmed() ? { status: "ok" } : null
  );

  useEffect(() => {
    if (!authed || result) return undefined;
    let live = true;
    confirmSession().then((r) => {
      if (live) setResult(r);
    });
    return () => {
      live = false;
    };
  }, [authed, result]);

  // Checked BEFORE `authed`: a refused session has already been cleared from
  // storage, and it must still land on the step it owes, not on plain login.
  if (result && result.status !== "ok" && result.status !== "unknown") {
    return redirectFor(result);
  }

  if (!authed) {
    return <Navigate to="/" replace />;
  }

  // Waiting for the server's answer — render nothing rather than the page.
  if (!result) return null;

  return children;
}

export default ProtectedRoute;
